/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Fragment } from 'react'
import { useDeviceMediaQuery } from '../Shared/UseDeviceMediaQuery/useDeviceMediaQuery'
import { Segment, Statistic, StatisticLabel, StatisticValue, Image } from 'semantic-ui-react'
import { ValueVariation } from '../Dashboard/Shared/ValueVariation'
import { useQuery } from '@tanstack/react-query'
import { getCatchmentAreaSummary } from '../../services/PointOfSale/PointOfSale.service'
import { useSearchParams } from 'react-router-dom-v5-compat'
import { periodToDate } from './period.service'
import { useTranslation } from 'react-i18next'

const PlatformStats = ({ platform, previousPlatform }) => {
    const [t] = useTranslation()

    return ((
        <div css={css`
        display: flex;
        flex-direction: column;
        gap: .5rem;
        flex: 1;
    `}>
            <div css={css`
            display: flex;
            align-items: center;
            gap: .5rem;
            font-weight: bold;
            font-size: 1em;
            margin-bottom: .25rem;
        `}>
                <Image
                  src={`/img/platforms/${platform.platformModule}.jpg`}
                  css={css`
                    width: 24px !important;
                    height: 24px !important;
                    border-radius: 50% !important;
                    object-fit: cover !important;
                `}
                />
                <span>{t(`catchment_area.platforms.${platform.platformModule}`)}</span>
            </div>
            <div css={css`
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: .5rem;
        `}>
                <Statistic size="mini">
                    <StatisticValue>
                        {platform.maxDistanceKm}km
                        <ValueVariation
                          value={platform.maxDistanceKm}
                          referenceValue={previousPlatform?.maxDistanceKm}
                          formatter={(value) => `${value.toFixed(2)}km`}
                        />
                    </StatisticValue>
                    <StatisticLabel>Distance max</StatisticLabel>
                </Statistic>
                <Statistic size="mini">
                    <StatisticValue>
                        {platform.averageDistanceKm}km
                        <ValueVariation
                          value={platform.averageDistanceKm}
                          referenceValue={previousPlatform?.averageDistanceKm}
                          formatter={(value) => `${value.toFixed(2)}km`}
                        />
                    </StatisticValue>
                    <StatisticLabel>Distance moyenne</StatisticLabel>
                </Statistic>
            </div>
        </div>
      )
    )
}
const Summary = () => {
    const {isMobile} = useDeviceMediaQuery()
    const [searchParams] = useSearchParams()
    const posId = searchParams.get('merchant')
    const from = searchParams.get('from')
    const to = searchParams.get('to')
    const scale = searchParams.get('scale') ?? 'week'
    const period = searchParams.get('period') ?? '0'

    const { from: prevFrom, to: prevTo } = periodToDate({ scale, period: +period - 1 })

    const {data: summary} = useQuery({
        queryKey: ['pointofsales', posId, 'catchment-area-summary', from, to],
        queryFn: async ({ signal }) => getCatchmentAreaSummary(posId, { from, to }, signal),
        enabled: !!posId,
    })

    const {data: previousSummary} = useQuery({
        queryKey: ['pointofsales', posId, 'catchment-area-summary', prevFrom, prevTo],
        queryFn: async ({ signal }) => getCatchmentAreaSummary(posId, { from: prevFrom, to: prevTo }, signal),
        enabled: !!posId,
    })

    if (!summary) {
        return null
    }

    const perPlatform = summary.perPlatform ?? []
    const hasPlatforms = perPlatform.length > 0

    return (
        <Segment css={css`display: flex; flex-direction: column; gap: 1rem;`}>
            <div css={css`
                display: grid;
                grid-template-columns: ${isMobile ? '1fr' : '1fr 1fr'};
                gap: .5rem;
            `}>
                <Statistic size="small">
                    <StatisticValue>
                        {summary.overall.maxDistanceKm}km
                        <ValueVariation
                            value={summary.overall.maxDistanceKm}
                            referenceValue={previousSummary?.overall?.maxDistanceKm}
                            formatter={(value) => `${value.toFixed(2)}km`}
                        />
                    </StatisticValue>
                    <StatisticLabel>Distance maximum de livraison</StatisticLabel>
                </Statistic>
                <Statistic size="small">
                    <StatisticValue>
                        {summary.overall.averageDistanceKm}km
                        <ValueVariation
                            value={summary.overall.averageDistanceKm}
                            referenceValue={previousSummary?.overall?.averageDistanceKm}
                            formatter={(value) => `${value.toFixed(2)}km`}
                        />
                    </StatisticValue>
                    <StatisticLabel>Distance moyenne de livraison</StatisticLabel>
                </Statistic>
            </div>
            {hasPlatforms && (
                <div css={css`
                    display: flex;
                    flex-direction: ${isMobile ? 'column' : 'row'};
                    gap: 1.5rem;
                    margin-top: .25rem;
                    padding-top: .75rem;
                    border-top: 1px solid rgba(0,0,0,.06);
                    align-items: flex-start;
                `}>
                    {perPlatform.map((platform, index) => {
                        const previousPlatform = previousSummary?.perPlatform?.find(
                            p => p.platformModule === platform.platformModule
                        )
                        return (
                            <Fragment key={platform.platformModule}>
                                {index > 0 && !isMobile && (
                                    <div css={css`
                                        width: 1px;
                                        background: rgba(0,0,0,.1);
                                        align-self: center;
                                        height: 60%;
                                    `} />
                                )}
                                <PlatformStats
                                    platform={platform}
                                    previousPlatform={previousPlatform}
                                    isMobile={isMobile}
                                />
                            </Fragment>
                        )
                    })}
                </div>
            )}
        </Segment>
    )
}

export { Summary }
